import React from 'react';
import { render, hydrate } from 'react-dom';
import { hot } from 'react-hot-loader';
import ErrorBoundary from 'core/components/ErrorBoundary';
import ContactSeller from 'core/components/ContactSeller/ContactSeller';
import RatingStars from 'core/components/RatingStars';
import Pagination from 'core/components/Pagination/PaginationContainer';
import Analytics from 'core/lib/Analytics';
import { captureException, captureMessage } from 'error_tracker';
import { LoginStatic } from 'core/components/Login';
import globalListeners from 'base/listeners/globals';
import SidebarSignupWidget from 'core/components/SidebarSignupWidget/SidebarSignupWidget';
import loopQuery from 'core/utils/loopQuery';
import getAppData, { getCustomData } from 'core/utils/getAppData';
import Header from 'core/components/Header/HeaderContainer';
import Footer from 'core/components/Footer/Footer';
import Notification from 'core/components/Notification';
import LoginModalHoc from 'core/hoc/LoginModalHoc';
import ForethoughtButton from './Forethought/ForethoughtButton';
import { QuestionTypes } from 'core/utils';
import { UserRecs } from 'core/components/Recs';
import ResponsiveProvider from 'core/providers/ResponsiveProvider';
import NoReviews from 'core/components/NoReviews';

class CoreComponents {
	static base() {
		CoreComponents.header();
		CoreComponents.footer();
		CoreComponents.forethoughtButton();
		CoreComponents.contactSeller();
		CoreComponents.pagination();
		CoreComponents.unhandledRejection();
		CoreComponents.staticLogin();
		CoreComponents.sidebarEmailSignup();
		CoreComponents.notifications();
		CoreComponents.loginWidget();
		CoreComponents.userRecs();
		CoreComponents.noReviews();
		Analytics.base();
		globalListeners();
	}

	static header() {
		const header = document.getElementById('react-header');
		if (! header) return null;

		const headerProps = getCustomData(window.cj.header_props, {});

		hydrate(<Header {...headerProps} />, header);
	}

	static footer() {
		const footer = document.getElementById('footer');
		if (! footer) return null;

		const footerProps = getCustomData(window.cj.footer_props, {});

		hydrate(<Footer {...footerProps} />, footer);
	}

	static forethoughtButton() {
		const forethoughtButton = document.getElementById('forethoughtButton');
		if (! forethoughtButton) return null;

		const props = {
			...forethoughtButton.dataset
		};

		hydrate(<ForethoughtButton {...props} />, forethoughtButton);
	}

	static staticLogin() {
		const target = document.getElementById('react-static-login');
		if (! target) return null;
		const loginView = target.getAttribute('data-login-view');
		render(<LoginStatic loginView={loginView} />, target);
	}

	static pagination() {
		const paginationEl = document.getElementById('pagination-react');
		if (! paginationEl) return null;

		let props = {
			...paginationEl.dataset,
		};

		render(<Pagination {...props} />, paginationEl);
	}

	static contactSeller() {
		const user = getCustomData(window.cj.user, {});
		const isUser = !!user.id;
		const contactBtns = document.querySelectorAll('.ContactSellerButton');
		if (! contactBtns) return;
		loopQuery(contactBtns, button => {
			const triggerLogin = window.cj.loginModal ? window.cj.loginModal.triggerLoginModal : () => console.error("Login modal not defined!");
			let props = {
				...button.dataset,
				fullWidth: button.dataset.fullWidth === 'true',
				triggerLogin,
			};
			if (props.listingId) {
				props.listingId = parseInt(props.listingId);
			}
			if (props.customerId) {
				props.customerId = parseInt(props.customerId);
			}
			if (props.purchase && props.purchase === 'pre_purchase') {
				props.questionType = QuestionTypes.pre_purchase;
			}
			render(
				<ErrorBoundary>
					<ContactSeller
						isLoggedIn={isUser}
						triggerLogin={triggerLogin}
						formProps={{ ...props }}
						{...props}
					/>
				</ErrorBoundary>,
				button
			);
		});
	}

	static noReviews() {
		// when in desktop mode, there are two elements with the same class
		// 1 in the sidebar that is not visible, and the one under the main image
		// that is visible
		const noReviewsEl = document.querySelectorAll('.listingRating-cta');
		if (!noReviewsEl.length) return;

		loopQuery(noReviewsEl, el => {
			let props = {
				anchorName: el.dataset.anchorTo,
			};
			render(
				<ErrorBoundary>
					<NoReviews {...props} />
				</ErrorBoundary>,
				el
			);
		});
	}

	static ratingStars() {
		const starsElements = document.querySelectorAll('.RatingStars');
		if (! starsElements.length) return;
		loopQuery(starsElements, star => {
			let data = star.dataset;
			let props = {
				...data,
				slug: data.slug,
				stars: parseFloat(data.stars) || 0,
				reviews: parseInt(data.reviews) || 0,
				short: data.short === "true",
				starsOnly: data.starsOnly === "true",
			};
			render(
				<ErrorBoundary>
					<RatingStars {...props} />
				</ErrorBoundary>,
				star
			);
		});
	}

	static sidebarEmailSignup() {
		const widgetElements = document.querySelectorAll('.sidebar-email-signup');
		if (! widgetElements.length) return;
		loopQuery(widgetElements, widgetElement => {
			const props = {
				minimal: widgetElement.classList.contains('minimal'),
			};
			render(
				<ErrorBoundary>
					<ResponsiveProvider>
						<SidebarSignupWidget {...props} />
					</ResponsiveProvider>
				</ErrorBoundary>,
				widgetElement
			);
		});
	}

	static unhandledRejection() {
		window.addEventListener('unhandledrejection', (err)  => {
			const reason = err.reason;
			if (reason instanceof Error) {
				captureException(reason);
			} else if (typeof reason === 'string') {
				captureMessage(reason);
			}
		});
	}

	static notifications() {
		const notifEl = document.getElementById('cj-notif');
		if (! notifEl) return;
		const message = notifEl.dataset.message;
		if (! message) return;
		render(<Notification message={message} delay={10000} />, notifEl);
	}

	static loginWidget() {
		// This needs to exist on the front-end listing page
		// to make available `window.cj.loginModal`
		// TODO: Remove once `wishlist_view`
		//				are ported to react
		const widgetEl = document.getElementById('login-modal-widget');
		if (!widgetEl) return;
		render(
			<ErrorBoundary>
				<LoginModalHoc contextUsage="backbone-widget-login">
					{({ triggerLoginModal }) => null}
				</LoginModalHoc>
			</ErrorBoundary>,
			widgetEl
		);
	}

	static userRecs() {
		const userRecsEl = document.getElementById('UserRecs');
		if (!userRecsEl) return;

		const user = getCustomData(window.cj.user, {});
		const pageType = window?.cj?.page_type || 'base';
		const urlParts = (window.location.pathname || "").split("/");
		const generalSlug = urlParts.length > 2 ? urlParts[2] : "";
		const data = { ...userRecsEl.dataset };
		if (data.noPadding === "true") {
			data.noPadding = true; // cast to bool
		}
		if (data.compact === "true") {
			data.compact = true; // cast to bool
		}
		if (data.limit) {
			data.limit = parseInt(data.limit) || 4;
		}
		const props = {
			...data,
			mobile: window?.cj?.isMobile,
			isLoggedIn: !!user.id,
			favoriteIds: getAppData('fav_listing_ids', []),
			tracking: { pt: pageType, gs: generalSlug },
		};
		render(
			<ErrorBoundary>
				<UserRecs {...props} />
			</ErrorBoundary>,
			userRecsEl
		);
	}
}

export default hot(module)(CoreComponents);
